import type { ColumnDef } from "@tanstack/react-table";
import { ListTableCard, ListTableEmpty } from "@/accounts-payable/common";
import { useGetVendors } from "@/accounts-payable/query";
import type { ApVendor } from "@/accounts-payable/types";
import { useGetQuickbooksConnection } from "@/business/quickbooks-query";
import {
  vendorCol,
  serviceTitanCol,
  aliasesCol,
  quickbooksVendorCol,
  expenseAccountCol,
} from "./cols";

const columns = [vendorCol, serviceTitanCol, aliasesCol];

const sized = (column: ColumnDef<ApVendor>, size: string) => ({
  ...column,
  meta: { ...column.meta, size },
});

// QuickBooks sits beside ServiceTitan: both say where a vendor's records go.
// Narrower, so the five fit the ~1,134px left beside the sidebar at 1440
// wide; the sizes are proportions, so without QuickBooks the three still
// share the width as before.
const quickbooksColumns = [
  sized(vendorCol, "230px"),
  sized(serviceTitanCol, "210px"),
  sized(quickbooksVendorCol, "210px"),
  sized(expenseAccountCol, "190px"),
  sized(aliasesCol, "260px"),
];

export const ApVendorsTab = ({
  businessId,
  selectedId = null,
  onSelect,
}: {
  businessId: string | null;
  selectedId?: string | null;
  onSelect: (id: string) => void;
}) => {
  const query = useGetVendors(businessId);
  // A disconnected business keeps its links, so only "never connected" hides
  // the QuickBooks columns.
  const { data: connection } = useGetQuickbooksConnection(businessId);
  const hasQuickbooks = !!connection && connection.status !== "not_connected";

  return (
    <ListTableCard
      label="Vendors"
      query={query}
      columns={hasQuickbooks ? quickbooksColumns : columns}
      selectedId={selectedId}
      onSelect={onSelect}
      empty={
        <ListTableEmpty
          title="No vendors yet"
          body="Vendors you receive invoices from will show up here."
        />
      }
      error={{
        title: "Couldn’t load vendors",
        body: "Please try again in a moment.",
      }}
    />
  );
};
